import { clsx } from 'clsx';
import moment from 'moment-timezone';
import { Col, Form, Row } from 'antd';
import React, { useEffect, useState } from 'react';
import { CreateProps, Create, useNotify, useRedirect } from 'react-admin';

import { axiosErrorToString } from '@utils';
import { financeDepositHttp } from '@network';
import { useCreateProps, useQuery, useRbacCreate } from '@hooks';
import { Container, Forbidden, Loader, PageHeader } from '@components';
import { CreateFinanceDepositParams, FinanceDepositOptions } from '@types';
import { FinanceDepositForm } from '../FinanceDepositForm';
import cls from './finance-deposit-create.module.css';

export const FinanceDepositCreate = (props: CreateProps) => {
  const query = useQuery();
  const notify = useNotify();
  const redirect = useRedirect();
  const rbacCreate = useRbacCreate();
  const createProps = useCreateProps();
  const [options, setOptions] = useState<FinanceDepositOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<CreateFinanceDepositParams>();
  const [submitted, setSubmitted] = useState(false);
  const [clientName, setClientName] = useState('');
  const accountId = query.get('id');
  const formValues: CreateFinanceDepositParams = {
    concept: '',
    accountId: accountId && options?.accounts.find(a => a.id === +accountId) ? +accountId : null,
    creditId: null,
    amount: 0,
    paymentDate: moment(new Date()).format('DD-MM-YYYY'),
    purpose: '',
    details: null,
    received: false,
    isUsd: false,
    exchangeRate: 0,
    amountUsd: 0,
  };

  const getOptions = async () => {
    try {
      const ops: FinanceDepositOptions = await financeDepositHttp.options();

      if (!ops) {
        throw new Error('The list of options has not been received');
      }
      setOptions(ops);
    } catch (e) {
      console.error(e);
    }
  };

  const submit = async (params: CreateFinanceDepositParams) => {
    if (loading) return;
    try {
      setLoading(true);
      const newDeposit = await financeDepositHttp.create(params);

      form.resetFields();
      setClientName('');
      setSubmitted(false);

      notify('Deposit has been created');
      redirect(`/finance-deposit/${newDeposit.id}`);
    } catch (e) {
      // @ts-ignore
      notify(axiosErrorToString(e), { type: 'error' });
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await getOptions();
    })();
  }, []);

  if (!options) {
    return <Loader center />;
  }

  if (!rbacCreate) {
    return <Forbidden />;
  }

  return (
    <Create {...props} {...createProps} className={clsx(cls.page, 'details-page-wrapper')}>
      <PageHeader
        title="Deposit"
        linkText="Back to all deposits"
        linkUrl="/finance-deposit"
      />

      <Container max>
        <Row gutter={24} className={cls.wrapper}>
          <Col xs={24} className={cls.wrapperCol}>
            <div className={cls.block}>
              <div className={cls.blockHeader}>Complete information</div>
              <Form
                form={form}
                layout="horizontal"
                initialValues={formValues}
              >
                <FinanceDepositForm
                  loading={loading}
                  setLoading={setLoading}
                  initial={formValues}
                  options={options}
                  onSubmit={submit}
                  form={form}
                  clientName={clientName}
                  setClientName={setClientName}
                  submitted={submitted}
                  setSubmitted={setSubmitted}
                />
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </Create>
  );
};
